import * as React from 'react'
import { useState, useEffect } from 'react'
import strings from './strings'
import ProgressionListItem from '../../molecules/progression-list-item'
import { Colors, Spacing, StackChildren, Text } from '@doordash/design-language'
import { get } from 'lodash'
import { IDVStatus, successIdvStatuses } from '../../../utilities/steps'
import { VehicleType, CheckrStatusResults } from '../../../constants'
import ScheduleOrientationStep from './ScheduleOrientationStep'
import ConfirmActivationStep from './ConfirmActivationStep'
import VerifyIdentityStep from './VerifyIdentityStep'
import CompleteDeliveryStep from './CompleteDeliveryStep'
import * as DA from '../../../typeModules/events'
import { Action } from 'redux'
import { IDVStatusResponse } from '../../../models/types'

type ProgressionCheckStep = {
  id: string
  dasherStepNumber: number
  component: React.ReactElement | null
  getTitle: (props: {
    isCompleted?: boolean
    identityVericationInProgress?: boolean
    identityInquiryId?: string
  }) => string
}

const progressionCheckStepNames = {
  ACCOUNT_CREATION_STEP: 'accountCreationStep',
  VERIFY_IDENTITY_STEP: 'verifyIdentityStep',
  SCHEDULE_ORIENTATION_STEP: 'scheduleOrientationStep',
  CONFIRM_ACTIVATION_STEP: 'confirmActivationStep',
  COMPLETE_DELIVERY_STEP: 'completeDeliveryStep',
}

const progressionCheckSteps: ProgressionCheckStep[] = [
  {
    id: progressionCheckStepNames.ACCOUNT_CREATION_STEP,
    dasherStepNumber: 1,
    component: null,
    getTitle: () => strings.progressionCheckAccountCreatedTitle(),
  },
  {
    id: progressionCheckStepNames.VERIFY_IDENTITY_STEP,
    dasherStepNumber: 2,
    component: <VerifyIdentityStep />,
    getTitle: props => {
      if (props.identityVericationInProgress && props.identityInquiryId) {
        return props.isCompleted
          ? strings.progressionActivationConfirmedTitle()
          : strings.progressionCheckVerifyIdentityInProgressTitle()
      }
      return props.isCompleted
        ? strings.progressionCheckIdentityVerifiedTitle()
        : strings.progressionCheckVerifyIdentityTitle()
    },
  },
  {
    id: progressionCheckStepNames.SCHEDULE_ORIENTATION_STEP,
    dasherStepNumber: 3,
    component: <ScheduleOrientationStep />,
    getTitle: props =>
      props.isCompleted
        ? strings.progressionCheckOrientationScheduledTitle()
        : strings.progressionCheckScheduleOrientationTitle(),
  },
  {
    id: progressionCheckStepNames.CONFIRM_ACTIVATION_STEP,
    dasherStepNumber: 4,
    component: <ConfirmActivationStep />,
    getTitle: props =>
      props.isCompleted
        ? strings.progressionActivationConfirmedTitle()
        : strings.progressionCheckConfirmActivationTitle(),
  },
  {
    id: progressionCheckStepNames.COMPLETE_DELIVERY_STEP,
    dasherStepNumber: 5,
    component: <CompleteDeliveryStep />,
    getTitle: () => strings.progressionCheckCompleteFirstDeliveryTitle(),
  },
]

const getDisplayStepNumberFromProgressionCheckSteps = (id: string): number => {
  const progressionCheckStep = progressionCheckSteps.find(
    progressionCheckStep => progressionCheckStep.id === id
  )
  return progressionCheckStep ? progressionCheckStep.dasherStepNumber : 1
}

type ProgressionCheckProps = {
  application: object
  scheduledOrientation?: {
    startTime: string
    address: string
  }
  uniqueLink?: string
  idvStatus?: IDVStatusResponse
  trackProgressionCheckRendered?: (
    action: DA.EventActions,
    step: string,
    applicantId: string,
    clientDriven: boolean
  ) => Action
}

const underReviewAndHardFailIdvStatuses: IDVStatus[] = [
  IDVStatus.PENDING,
  IDVStatus.NEEDS_REVIEW,
  IDVStatus.FAILED,
  IDVStatus.DECLINED,
]

// Gets the active step index number
const getActiveStep = (
  scheduledOrientation: {
    startTime: string
    address: string
  },
  idvStatus: IDVStatus,
  identityVerificationStatus: IDVStatus,
  backgroundCheck: CheckrStatusResults
): string => {
  const isIdentityVerified =
    successIdvStatuses.includes(idvStatus) &&
    successIdvStatuses.includes(identityVerificationStatus)
  if (!isIdentityVerified) {
    return progressionCheckStepNames.VERIFY_IDENTITY_STEP
  }

  const hasScheduledOrientation =
    scheduledOrientation &&
    scheduledOrientation.startTime &&
    scheduledOrientation.address
  if (!hasScheduledOrientation) {
    return progressionCheckStepNames.SCHEDULE_ORIENTATION_STEP
  }

  const clearedBackgroundCheck = backgroundCheck === CheckrStatusResults.Clear
  if (!clearedBackgroundCheck) {
    return progressionCheckStepNames.CONFIRM_ACTIVATION_STEP
  }

  return progressionCheckStepNames.COMPLETE_DELIVERY_STEP
}

const ProgressionCheck: React.FC<ProgressionCheckProps> = ({
  application,
  scheduledOrientation,
  uniqueLink,
  idvStatus,
  trackProgressionCheckRendered,
}) => {
  const identityVerificationStatus = get(
    application,
    'identityVerificationStatus',
    IDVStatus.NOT_COMPLETED
  )
  const identityInquiryId = get(application, 'identityInquiryId')
  const vehicleType = get(application, 'vehicleType', VehicleType.Car)
  const backgroundCheck = get(application, 'backgroundCheckResults')
  const [currentStep, setCurrentStep] = useState(
    progressionCheckStepNames.VERIFY_IDENTITY_STEP
  )

  useEffect(() => {
    setCurrentStep(
      getActiveStep(
        scheduledOrientation,
        idvStatus.value,
        identityVerificationStatus,
        backgroundCheck
      )
    )
  }, [
    backgroundCheck,
    scheduledOrientation,
    idvStatus,
    identityVerificationStatus,
    identityInquiryId,
  ])

  useEffect(() => {
    trackProgressionCheckRendered(
      DA.EventActions.Rendered,
      currentStep,
      uniqueLink,
      true
    )
  }, [trackProgressionCheckRendered, currentStep, uniqueLink])

  return (
    <StackChildren size={Spacing.Large}>
      <Text color={Colors.TextPrimary} styles={Text.Styles.MajorPageTitle}>
        {strings.progressionCheckTitle()}
      </Text>
      <div>
        {progressionCheckSteps.map(
          (progressionCheckStep: ProgressionCheckStep) => {
            const stepIndex = progressionCheckStep.dasherStepNumber
            const currentStepNumber = getDisplayStepNumberFromProgressionCheckSteps(
              currentStep
            )
            const isStepCompleted: boolean = stepIndex < currentStepNumber
            const stepComponent = progressionCheckStep.component
            const stepData = {
              vehicleType,
              uniqueLink,
              identityVerificationStatus,
              identityInquiryId,
              idvStatus: idvStatus.value,
            }
            const identityVericationInProgress = !underReviewAndHardFailIdvStatuses.includes(
              identityVerificationStatus
            )

            const title = progressionCheckStep.getTitle({
              identityVericationInProgress,
              identityInquiryId,
              isCompleted: isStepCompleted,
            })

            return (
              <ProgressionListItem
                isActive={stepIndex === currentStepNumber}
                title={title}
                isComplete={isStepCompleted}
                key={stepIndex}
                step={stepIndex}
              >
                {stepComponent && React.cloneElement(stepComponent, stepData)}
              </ProgressionListItem>
            )
          }
        )}
      </div>
    </StackChildren>
  )
}

export default ProgressionCheck
